import { useEffect, useRef, useState } from "react";
import { addDoc, collection, onSnapshot, serverTimestamp } from "firebase/firestore";
import { Check, ChevronDown, ChevronRight, Plus, Search, X } from "lucide-react";
import { auth, db } from "../firebase";
import "./AssistanceDailyNotes.css";

const categories = {
  important: { label: "Viktig information", color: "#ED233B", tint: "#FFE5E8" },
  visit: { label: "Besök genomfört", color: "#087EFF", tint: "#E5F0FF" },
  other: { label: "Övrigt", color: "#08BF72", tint: "#DCFCE7" },
  observation: { label: "Observation", color: "#8950FF", tint: "#EFE7FF" },
  deviation: { label: "Avvikelser", color: "#F5AD00", tint: "#FFF2CC" },
};
const noteCategories = { ...categories, information: categories.other, medicine: { label: "Medicin", color: "#F5AD00", tint: "#FFF2CC" } };
const dateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

function NoteDialog({ title, saving, onClose, children }) {
  const dialog = useRef(null);
  useEffect(() => { dialog.current.showModal(); }, []);
  return <dialog ref={dialog} className="daily-notes__dialog" aria-labelledby="daily-notes-dialog-title" onCancel={(event) => { event.preventDefault(); if (!saving) onClose(); }}>
    <div className="daily-notes__dialog-heading"><h3 id="daily-notes-dialog-title">{title}</h3><button type="button" aria-label="Stäng" disabled={saving} onClick={onClose}><X size={22} /></button></div>
    {children}
  </dialog>;
}

export default function AssistanceDailyNotes({ companyUid }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [category, setCategory] = useState("other");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [message, setMessage] = useState("");
  const saveLock = useRef(false);
  const today = dateKey(new Date());
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);

  useEffect(() => {
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "assistanceDailyNotes"), (snapshot) => {
      setData({ companyUid, notes: snapshot.docs.map((item) => ({ ...item.data(), id: item.id })) });
      setError("");
    }, () => setError("Kunde inte hämta daganteckningar. Kontrollera anslutningen och försök igen."));
  }, [companyUid]);

  useEffect(() => {
    if (!companyUid || !creating) return;
    return onSnapshot(collection(db, "users", companyUid, "clients"), (snapshot) => {
      const clients = snapshot.docs.map((item) => {
        const details = item.data();
        const firstName = typeof details.firstName === "string" ? details.firstName.trim() : "";
        const lastName = typeof details.lastName === "string" ? details.lastName.trim() : "";
        return { id: item.id, name: `${firstName} ${lastName}`.trim(), sortName: `${lastName} ${firstName}`.trim() };
      }).filter((client) => client.name).sort((a, b) => a.sortName.localeCompare(b.sortName, "sv"));
      setClientData({ companyUid, clients, error: "" });
    }, () => setClientData({ companyUid, clients: [], error: "Kunde inte hämta klienterna. Stäng och öppna formuläret för att försöka igen." }));
  }, [companyUid, creating]);

  const notes = data && data.companyUid === companyUid ? data.notes : [];
  const clients = clientData && clientData.companyUid === companyUid ? clientData.clients : [];
  const clientError = clientData && clientData.companyUid === companyUid ? clientData.error : "";
  const clientsLoading = Boolean(companyUid) && clientData?.companyUid !== companyUid;
  const selectedClient = clients.find((client) => client.id === clientId);
  const matchingClients = clients.filter((client) => client.name.toLocaleLowerCase("sv").includes(clientSearch.trim().toLocaleLowerCase("sv")));
  const matching = notes.filter((note) => (note.clientName || "").toLocaleLowerCase("sv").includes(search.trim().toLocaleLowerCase("sv"))
    && (filter !== "today" || note.date === today)
    && (filter !== "mine" || note.authorUid === auth.currentUser?.uid)
    && (filter !== "important" || note.category === "important"))
    .sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
  const dates = [...new Set(matching.map((note) => note.date))];
  const close = () => { if (!saveLock.current) { setCreating(false); setSelected(null); } };
  const startCreating = () => {
    setClientDropdownOpen(false); setClientData(null); setClientId(""); setClientSearch(""); setCategory("other"); setText(""); setSaveError(""); setMessage(""); setCreating(true);
  };
  const save = async (event) => {
    event.preventDefault();
    const user = auth.currentUser;
    if (!companyUid || !user || !selectedClient || !text.trim() || saveLock.current) return;
    saveLock.current = true; setSaving(true); setSaveError("");
    const now = new Date();
    try {
      await addDoc(collection(db, "users", companyUid, "assistanceDailyNotes"), {
        clientId: selectedClient.id, clientName: selectedClient.name, text: text.trim(), category,
        authorUid: user.uid, authorName: user.displayName || user.email || "Personal",
        date: dateKey(now), time: now.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }), createdAt: serverTimestamp(),
      });
      setCreating(false); setText(""); setClientId(""); setMessage("Daganteckningen har sparats.");
    } catch { setSaveError("Kunde inte spara anteckningen. Texten finns kvar, försök igen."); }
    finally { saveLock.current = false; setSaving(false); }
  };

  if (!companyUid) return <p role="status">Välj ett företag för att skapa och visa daganteckningar.</p>;
  return <div className="daily-notes">
    <label className="daily-notes__search"><Search size={22} /><input type="search" aria-label="Sök på klientens namn" placeholder="Sök på klientens namn" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
    <div className="daily-notes__filters" aria-label="Filtrera daganteckningar">
      {[["all", "Alla"], ["today", "Idag"], ["mine", "Mina"], ["important", "Viktiga"]].map(([id, label]) => <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}>{label}</button>)}
    </div>
    <button className="daily-notes__primary daily-notes__new" type="button" onClick={startCreating}><Plus size={24} />Ny daganteckning</button>
    {message && <p role="status">{message}</p>}
    {error && <p role="alert">{error}</p>}
    {!error && data?.companyUid !== companyUid && <p role="status">Hämtar daganteckningar…</p>}
    {!error && data?.companyUid === companyUid && !matching.length && <p>{notes.length ? "Inga daganteckningar matchar din sökning." : "Inga daganteckningar ännu."}</p>}
    {dates.map((day) => <section key={day} className="daily-notes__group">
      <h3>{day === today ? "Idag, " : day === dateKey(yesterday) ? "Igår, " : ""}{new Date(`${day}T12:00:00`).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}</h3>
      {matching.filter((note) => note.date === day).map((note) => {
        const appearance = noteCategories[note.category] || categories.other;
        return <button type="button" key={note.id} className={`daily-notes__card${note.category === "important" ? " is-important" : ""}`} style={{ "--note-color": appearance.color, "--note-tint": appearance.tint }} onClick={() => setSelected(note)}>
          <span className="daily-notes__avatar" aria-hidden="true">{(note.clientName || "").trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join("").toUpperCase()}</span>
          <span className="daily-notes__body"><span className="daily-notes__heading"><strong>{note.clientName}</strong><span className="daily-notes__meta">{note.time}</span><span className="daily-notes__badge">{appearance.label}</span></span>
            <span className="daily-notes__preview">{note.text}</span><span className="daily-notes__author">Skrivet av: {note.authorName}</span></span><ChevronRight size={22} aria-hidden="true" />
        </button>;
      })}
    </section>)}
    {creating && <NoteDialog title="Ny daganteckning" saving={saving} onClose={close}>
      <form onSubmit={save} className="daily-notes__form">
        <fieldset disabled={saving}>
          <div className="daily-notes__client-picker" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setClientDropdownOpen(false); }} onKeyDown={(event) => { if (event.key === "Escape" && clientDropdownOpen) { event.preventDefault(); event.stopPropagation(); setClientDropdownOpen(false); event.currentTarget.querySelector("button").focus(); } }}>
            <span id="daily-notes-client-label" className="daily-notes__label">Klient</span>
            <button type="button" className="daily-notes__client-trigger" aria-labelledby="daily-notes-client-label daily-notes-client-value" aria-expanded={clientDropdownOpen} aria-controls="daily-notes-client-options" disabled={clientsLoading || !clients.length} onClick={() => { setClientSearch(""); setClientDropdownOpen((open) => !open); }}>
              <span id="daily-notes-client-value">{clientsLoading ? "Hämtar klienter…" : selectedClient?.name || "Välj klient"}</span><ChevronDown size={20} aria-hidden="true" />
            </button>
            {clientDropdownOpen && <div id="daily-notes-client-options" className="daily-notes__client-options">
              <div className="daily-notes__client-search"><Search size={20} aria-hidden="true" /><input autoFocus type="search" aria-label="Sök klient i listan" value={clientSearch} onChange={(event) => setClientSearch(event.target.value)} placeholder="Sök klientens namn…" /></div>
              <div className="daily-notes__client-list">
                {matchingClients.map((client) => <button type="button" key={client.id} aria-pressed={client.id === clientId} onClick={(event) => { setClientId(client.id); setClientDropdownOpen(false); event.currentTarget.closest(".daily-notes__client-picker").querySelector("button").focus(); }}>
                  <span>{client.name}</span>{client.id === clientId && <Check size={20} aria-hidden="true" />}
                </button>)}
                {!matchingClients.length && <p>Ingen klient matchar din sökning.</p>}
              </div>
            </div>}
          </div>
          {clientError && <p role="alert">{clientError}</p>}
          {!clientsLoading && !clientError && !clients.length && <p>Inga klienter finns i Mina klienter. Lägg till en klient där först.</p>}
          <label>Typ av anteckning<select value={category} onChange={(event) => setCategory(event.target.value)}>{Object.entries(categories).map(([id, option]) => <option key={id} value={id}>{option.label}</option>)}</select></label>
          <label>Anteckning<textarea required rows={6} maxLength={10000} value={text} onChange={(event) => setText(event.target.value)} placeholder="Skriv din daganteckning…" /></label>
          {saveError && <p role="alert">{saveError}</p>}
          {!auth.currentUser && <p role="alert">Logga in för att skriva en daganteckning.</p>}
          <button className="daily-notes__primary" type="submit" disabled={saving || !auth.currentUser || !selectedClient || !text.trim()}>{saving ? "Sparar…" : "Spara daganteckning"}</button>
        </fieldset>
      </form>
    </NoteDialog>}
    {selected && <NoteDialog title={selected.clientName} onClose={close}>
      <p className="daily-notes__meta">{selected.date} · {selected.time} · {(noteCategories[selected.category] || categories.other).label}</p>
      <p className="daily-notes__full-text">{selected.text}</p><p className="daily-notes__author">Skrivet av: {selected.authorName}</p>
    </NoteDialog>}
  </div>;
}
